import { FC, useEffect, useMemo, useState } from "react";
import DefaultImage from '../../../../assets/img/default-image.svg';
import Roles from "../../../../models/roles";
import { formatAllowance, formatDate } from "../../../../models/vacancy";
import { useAppSelector as useSelector } from "../../../../store";
import { capitalizeName, extractSuburbFromAddress } from "../../../../utils/stringUtils";
import PopupModal from "../../../UI/Popup";
import StarRating from "../../../UI/StarRating";
import ClientOnlineInterview from "../ViewProfileSteps/ClientOnlineInterview";
import ClientPersonalDetails from "../ViewProfileSteps/ClientPersonalDetails";
import ClientQualification from "../ViewProfileSteps/ClientQualification";

interface ViewProfileProps {
  showProfileModal: boolean;
  onClickClose: () => void;
}

const ViewProfile: FC<ViewProfileProps> = ({
  showProfileModal,
  onClickClose,
}) => {
    const reviewCandidateData = useSelector((state) => state.superadmin.reviewCandidateData)
    const selectedVacancyDetails = useSelector(
        (state) => state.vacancy.selectedVacancy
    );
    const selectedCentre = useSelector((state) => state.vacancy.selectedCentreDetails)
    const user = useSelector((state) => state.auth.user)

    const isAdmin = useMemo(() =>
        user?.role.name === Roles.SUPER_ADMIN || 
        user?.role.name === Roles.GENERAL_ADMIN
    , [user])

    const [showPersonalDetails, setShowPersonalDetails] = useState<boolean>(true);
    const [showQualifications, setShowQualifications] = useState<boolean>(false);
    const [showOnlineInterview, setShowOnlineInterview] = useState<boolean>(false);
    const [showComplainceCheck, setShowComplainceCheck] = useState<boolean>(false);

    useEffect(() => {
        handleSectionNavigation("personalDetails");
    }, []);

    /**
     * Handle for section navigation based on the selected section.
     * @param section - The section to navigate to.
     */
    const handleSectionNavigation = (section: string) => {
        setShowPersonalDetails(section === "personalDetails");
        setShowQualifications(section === "qualifications");
        setShowOnlineInterview(section === "online_interview");
        // setShowComplainceCheck(section === "complaince_check");
    };

    const getSectionClass = (section: string): string => {
        if (
            (section === "personalDetails" && showPersonalDetails) ||
            (section === "qualifications" && showQualifications) || 
            (section === "online_interview" && showOnlineInterview)
            // (section === "complaince_check" && showComplainceCheck)
        ) {
            return "fw-700";
        }

        return "";
    };

    /**
     * * Generates the steps for the candidate review process based on the current stage.
     * @param currentStage - The current stage of the candidate's profile.
     * @returns
     */
    const generateSteps = (currentStage: number) => {
        const steps = [
            {
                name: "Personal Details",
                stage: 1,
                section_name: "personalDetails",
                key: "personal_details",
            },
            {
                name: "Qualifications",
                stage: 1,
                section_name: "qualifications",
                key: "qualification",
            },
            {
                name: "Online Interview",
                stage: 1,
                section_name: "online_interview",
                key: "online_interview",
            },
            // {
            //     name: "Complaince Check",
            //     stage: 2,
            //     section_name: "complaince_check",
            //     key: "complaince_check",
            // }
        ];

        return steps
        //   .filter((step) => step.stage <= currentStage)
          .map((step) => (
            <div className="d-flex gap-10 align-items-center" key={step.name}>
              <div
                className={`fs-16 cursor-pointer ${getSectionClass(
                  step.section_name
                )}`}
                onClick={() => handleSectionNavigation(step.section_name)}
              >
                {step.name}
              </div>
            </div>
        ));
    };

    /**
     * * Handles the download of documents by creating a temporary link and triggering a click event.
     * @param url - The URL of the document to be downloaded.
     */
    const handleDownloadDocument = (url: string | null) => {
        if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "document.pdf"); // Set the file name for download
        link.target = "_blank"; // Open in a new tab
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        } else {
        console.error("No URL provided for download.");
        }
    };

    /**
     * Convert a snake_case string to Title Case.
     * @param snakeCaseString - The snake_case string to convert.
     * @returns The converted Title Case string.
     */
    const convertToTitleCase = (
        snakeCaseString: string | null | undefined
    ): string => {
        if (!snakeCaseString || snakeCaseString.trim() === "") {
        return "";
        }
        return snakeCaseString
        .split("_") // Split the string by underscores
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
        .join(" "); // Join the words with spaces
    };

    return (
        <>
            <PopupModal
                visible={showProfileModal}
                showCloseBtn={true}
                onClickClose={onClickClose}
                customWidth={1000}
            >
                <div className="d-flex flex-column gap-20">
                <div className="d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
                    <div className="w-130px h-130px radius-50px">
                    <img
                        alt="Candidate"
                        width="130px"
                        height="130px"
                        className="img-responsive rounded-circle object-fit-cover"
                        src={reviewCandidateData?.photo || DefaultImage}
                    />
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-30 align-items-lg-center">
                    <div className="d-flex flex-column gap-10 min-w-280px">
                        <div className="fs-30 fw-400 text-black">{capitalizeName(reviewCandidateData?.first_name ?? "")} {capitalizeName(reviewCandidateData?.last_name ?? "")}</div>
                        <div className="d-flex flex-column gap-10">
                        <div className="d-flex gap-10 align-items-center">
                            <StarRating
                                rating={
                                isAdmin
                                    ? reviewCandidateData?.rating?.average_rating || 0
                                    : reviewCandidateData?.rating?.latest_rating ||
                                    reviewCandidateData?.rating?.average_rating ||
                                    0
                                }
                                color={
                                isAdmin || !reviewCandidateData?.rating?.latest_rating
                                    ? "#aaaaaa"
                                    : "#51C2B9"
                                }
                            />
                            {!isAdmin &&
                                !reviewCandidateData?.rating?.latest_rating &&
                                reviewCandidateData?.rating?.average_rating && (
                                <div className="fs-10 fw-400 text-grey-dark">Average Rating</div>
                                )}
                            <div className={`border ${
                                    reviewCandidateData?.isPreferred
                                    ? "border-teal-dark bg-teal-pale"
                                    : "border-off-white"
                                } py-4px px-8px radius-8px d-flex gap-3 align-items-center`}>
                                <div className={`fs-13 fw-400 ${
                                    reviewCandidateData?.isPreferred
                                        ? "text-teal-dark"
                                        : "text-off-white"
                                    }`}>
                                    Preferred
                                </div>
                                {/* <Cross /> */}
                            </div>
                        </div>
                        <div className="d-flex flex-column max-w-400px">
                            <div className="fs-13 fw-400 text-black">
                            {reviewCandidateData?.qualifications?.position_names ?? "-"}
                            </div>
                            <div className="fs-13 fw-400 text-black mt-3">
                            Lives in {extractSuburbFromAddress(reviewCandidateData?.address ?? "")} {reviewCandidateData?.distance_km && reviewCandidateData?.distance_km > 0 ? `(${reviewCandidateData.distance_km} km away)` : ""}
                            </div>
                        </div>
                        {
                            (user?.role.name === Roles.SUPER_ADMIN || user?.role.name === Roles.GENERAL_ADMIN) && (
                                <div className="d-flex gap-10 align-items-center">
                                    <div className="bg-teal-muted border border-black radius-8px">
                                    <div className="fs-13 fw-400 text-black lh-24px px-5px">
                                        {(reviewCandidateData?.profile?.loading_amount ?? 0) >= 0 && "+"}{(reviewCandidateData?.profile?.loading_amount ?? 0) > 0 ? formatAllowance(reviewCandidateData?.profile?.loading_amount ?? 0) : "$0.00"}
                                    </div>
                                    </div>
                                    <div className="fs-13 fw-400 text-grey-medium">
                                    Loading {reviewCandidateData?.profile?.loading_amount_added_on === null ? "not" : ""} added {formatDate(reviewCandidateData?.profile?.loading_amount_added_on ? new Date(reviewCandidateData.profile.loading_amount_added_on) : null)}
                                    </div>
                                </div>
                            )
                        }
                        
                        </div>
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-40 gap-mobile-20 align-items-lg-center">
                        <div className="d-flex flex-column gap-5">
                        <div className="fs-22 fw-400 text-black">{reviewCandidateData?.past_jobs_count_with_out_centre ?? 0}</div>
                        <div className="fs-13 fw-400 text-grey-medium">
                            Past Jobs at
                            <br />
                            Any Centre
                        </div>
                        </div>
                        <div className="d-flex flex-column gap-5">
                        <div className="fs-22 fw-400 text-black">{reviewCandidateData?.past_jobs_count ?? 0}</div>
                        <div className="fs-13 fw-400 text-grey-medium">
                            Past Jobs at
                            <br />
                            {reviewCandidateData?.centre?.business_name ?? ""}
                        </div>
                        </div>
                        <div className="d-flex flex-column gap-5">
                        <div className="fs-22 fw-400 text-black">{reviewCandidateData?.last_job_date ?? "-"}</div>
                        <div className="fs-13 fw-400 text-grey-medium">
                            Last Job at
                            <br />
                            {reviewCandidateData?.centre?.business_name ?? ""}
                        </div>
                        </div>
                    </div>
                    </div>
                </div>
                <div className="h-1px w-100 bg-grey-light"></div>
                <div className="d-flex flex-column flex-lg-row gap-50 gap-mobile-20">
                    <div className="min-w-150px d-flex flex-column gap-20 fs-16 fw-400 text-black">
                        {generateSteps(1)}
                    </div>
                    <div className="w-1px bg-off-white"></div>
                    {/** Personal Details */}
                    <ClientPersonalDetails
                        handleDownloadDocument={handleDownloadDocument}
                        showPersonalDetails={showPersonalDetails}
                        reviewCandidateData={reviewCandidateData}
                    />
                    {/** Qualification */}
                    <ClientQualification
                        showQualifications={showQualifications}
                        reviewCandidateData={reviewCandidateData}
                        convertToTitleCase={convertToTitleCase}
                    />
                    
                    { /** Online Interview */}
                    <ClientOnlineInterview 
                        showOnlineInterview={showOnlineInterview}
                        reviewCandidateData={reviewCandidateData}
                    />

                    {/* <ClientComplianceCheck
                        showComplianceCheck={showComplainceCheck}
                        reviewCandidateData={reviewCandidateData}
                    /> */}
                    
                </div>
                </div>
            </PopupModal>
        </>
  );
};

export default ViewProfile;
